import { cn } from "@/lib/utils";
import {
  Fragment,
  type MouseEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  MAGNIFY_LEVELS,
  calculateMagnifyDimensions,
} from "./MagnifyImage.util";

interface MagnifyImageProps {
  src: string;
  className?: string;
  /** If provided, the popup is pinned to the left of this element's left edge. */
  magnifyImageOffsetRef?: RefObject<Element | null>;
}

const MAGNIFIER_DELAY_MS = 200;

export const MagnifyImage = ({
  className,
  src,
  magnifyImageOffsetRef,
}: MagnifyImageProps) => {
  const ref = useRef<HTMLImageElement>(null);
  const delayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showMagnifier, setShowMagnifier] = useState(false);
  const [[x, y], setXY] = useState([0, 0]);
  const [zoomLevelIndex, setZoomLevelIndex] = useState(0);

  const { magnifier, image, enlargedImage } = calculateMagnifyDimensions(
    ref.current,
    { x, y },
    MAGNIFY_LEVELS[zoomLevelIndex],
  );

  const handleMouseEnter = () => {
    if (delayTimer.current) {
      clearTimeout(delayTimer.current);
      delayTimer.current = null;
    }
    delayTimer.current = setTimeout(
      () => setShowMagnifier(true),
      MAGNIFIER_DELAY_MS,
    );
  };

  const handleMouseLeave = (e: MouseEvent) => {
    e.preventDefault();
    if (delayTimer.current) {
      clearTimeout(delayTimer.current);
      delayTimer.current = null;
    }
    setShowMagnifier(false);
  };

  useEffect(() => {
    // Prevent delayed state updates after this component unmounts.
    return () => {
      if (delayTimer.current) {
        clearTimeout(delayTimer.current);
        delayTimer.current = null;
      }
    };
  }, []);

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    setZoomLevelIndex((prev) => (prev + 1) % MAGNIFY_LEVELS.length);
  };

  const handleMouseMove = (e: MouseEvent) => {
    const el = e.currentTarget;
    const { top, left } = el.getBoundingClientRect();

    const mouseX = e.pageX - left - window.scrollX;
    const mouseY = e.pageY - top - window.scrollY;

    // Constrain the magnifier center to the actual rendered image content area,
    // accounting for object-contain letterboxing (offsetX/Y).
    const x = Math.min(
      image.offsetX + image.width - magnifier.width / 2,
      Math.max(image.offsetX + magnifier.width / 2, mouseX),
    );

    const y = Math.min(
      image.offsetY + image.height - magnifier.height / 2,
      Math.max(image.offsetY + magnifier.height / 2, mouseY),
    );

    setXY([x, y]);
  };

  // Position the popup to the left of magnifyImageOffsetRef when provided,
  // otherwise center it over the image.
  const POPUP_GAP = 16;
  const imgRect = ref.current?.getBoundingClientRect();
  const popupPositionStyle: React.CSSProperties = magnifyImageOffsetRef
    ? {
        right:
          window.innerWidth -
          (magnifyImageOffsetRef.current?.getBoundingClientRect().left ?? 0) +
          POPUP_GAP,
      }
    : {
        left: (imgRect?.left ?? 0) + (imgRect?.width ?? 0) / 2,
        top: (imgRect?.top ?? 0) + (imgRect?.height ?? 0) / 2,
        transform: "translate(-50%, -50%)",
      };

  return (
    <div className="relative flex w-fit m-auto overflow-hidden">
      {ref.current && (
        <Fragment>
          <div
            data-testid="magnify-image-magnifier"
            className={cn(
              "absolute top-0 left-0 pointer-events-none transition-opacity duration-150",
              showMagnifier ? "opacity-100" : "opacity-0",
            )}
            style={{
              boxShadow: "0px 0px 0px 2000px rgba(0, 0, 0, 0.6)",
              height: magnifier.height,
              width: magnifier.width,
              transform: `translate(${x - magnifier.width / 2}px, ${y - magnifier.height / 2}px)`,
            }}
          />
        </Fragment>
      )}

      <a
        className="contents"
        href={src}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          data-testid="magnify-preview-img"
          className={cn("object-contain cursor-zoom-in", className)}
          ref={ref}
          src={src}
          alt="uploaded file"
          onClick={handleClick}
          onKeyDown={() => {}}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        />
      </a>

      {ref.current &&
        createPortal(
          <div
            data-testid="magnify-image-enlarged"
            className={cn(
              "fixed pointer-events-none border border-gray-400 rounded-md bg-gray-100 z-50 shadow-2xl transition-opacity duration-150",
              magnifyImageOffsetRef && "top-1/2 -translate-y-1/2",
              showMagnifier ? "opacity-100" : "opacity-0",
            )}
            style={{
              ...popupPositionStyle,
              height: enlargedImage.height,
              width: enlargedImage.width,
              backgroundImage: `url('${src}')`,
              backgroundRepeat: "no-repeat",
              backgroundSize: `${enlargedImage.backgroundSize.x}px ${enlargedImage.backgroundSize.y}px`,
              backgroundPositionX: `${enlargedImage.backgroundPosition.x}px`,
              backgroundPositionY: `${enlargedImage.backgroundPosition.y}px`,
            }}
          />,
          document.body,
        )}
    </div>
  );
};
